import { format } from "date-fns";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useTranslate, type Identifier, type SortPayload } from "ra-core";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDateLocale } from "../../personal/useDateLocale";
import type { Task } from "../../types";
import { PriorityBadge } from "../PriorityBadge";
import { LoadMore } from "../LoadMore";
import { TaskMeta } from "./TaskMeta";
import { TaskStatusBadge } from "./TaskStatusBadge";
import { useCardNames, useInfiniteTasks } from "./useTasksData";

/** Columns the server can sort on. */
type SortField = "text" | "due_date" | "created_at";

export interface TaskListProps {
  /** Server filter, including the status. */
  filter: Record<string, unknown>;
  salesNames: Map<Identifier, string>;
  onOpen: (task: Task) => void;
}

/** Task table loading 25 tasks at a time while the page is scrolled. */
export const TaskList = ({ filter, salesNames, onOpen }: TaskListProps) => {
  const translate = useTranslate();
  const { dateFnsLocale } = useDateLocale();
  const [sort, setSort] = useState<SortPayload>({
    field: "due_date",
    order: "ASC",
  });
  const {
    tasks,
    total,
    isPending,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteTasks(filter, sort);
  const cardNames = useCardNames(tasks);

  const sortableHeader = (field: SortField, label: string) => (
    <TableHead>
      <button
        type="button"
        className="flex items-center gap-1 font-medium"
        onClick={() =>
          setSort((current) => ({
            field,
            order:
              current.field === field && current.order === "ASC"
                ? "DESC"
                : "ASC",
          }))
        }
      >
        {label}
        {sort.field === field &&
          (sort.order === "ASC" ? (
            <ArrowUp className="size-3" />
          ) : (
            <ArrowDown className="size-3" />
          ))}
      </button>
    </TableHead>
  );

  if (isPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (total === 0) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground">
        {translate(
          Object.keys(filter).length > 0
            ? "crm.operations.tasks.empty_filtered"
            : "crm.operations.tasks.empty",
        )}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        {translate("crm.operations.tasks.count", { smart_count: total })}
      </p>
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              {sortableHeader(
                "text",
                translate("crm.operations.tasks.fields.title"),
              )}
              <TableHead>
                {translate("crm.operations.tasks.fields.card")}
              </TableHead>
              <TableHead>
                {translate("crm.operations.tasks.fields.status")}
              </TableHead>
              <TableHead>
                {translate("crm.operations.tasks.fields.priority")}
              </TableHead>
              <TableHead>
                {translate("crm.operations.tasks.fields.assignee")}
              </TableHead>
              {sortableHeader(
                "due_date",
                translate("crm.operations.tasks.fields.due_date"),
              )}
              {sortableHeader(
                "created_at",
                translate("crm.operations.tasks.fields.created_at"),
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.map((task) => (
              <TableRow
                key={task.id}
                className="cursor-pointer"
                onClick={() => onOpen(task)}
                data-testid="task-row"
              >
                <TableCell className="max-w-72 truncate font-medium">
                  {task.text}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {task.contact_id != null
                    ? cardNames.get(task.contact_id)
                    : translate("crm.operations.tasks.free")}
                </TableCell>
                <TableCell>
                  <TaskStatusBadge status={task.status ?? "todo"} />
                </TableCell>
                <TableCell>
                  <PriorityBadge priority={task.priority ?? "low"} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {task.sales_id != null ? salesNames.get(task.sales_id) : ""}
                </TableCell>
                <TableCell>
                  {task.due_date ||
                  task.estimated_hours != null ||
                  task.spent_hours != null ? (
                    <TaskMeta task={task} />
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {translate("crm.operations.tasks.no_due")}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {task.created_at
                    ? format(new Date(task.created_at), "P", {
                        locale: dateFnsLocale,
                      })
                    : ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <LoadMore
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
    </div>
  );
};
